import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Building2, Edit2, Save, X } from 'lucide-react';

const empty = {
  company_name: '', legal_name: '', gstin: '', pan: '', registration_no: '',
  address: '', city: '', state: '', pincode: '', country: 'India',
  phone: '', email: '', website: '',
  financial_year_start: '04-01', currency: 'INR', notes: '',
};

// Read-only field row — clean two-column display
const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 14, color: '#0f172a', minHeight: 18, wordBreak: 'break-word' }}>
      {value && String(value).trim() ? value : <span style={{ color: '#cbd5e1' }}>—</span>}
    </div>
  </div>
);

export default function Company() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/company').then((r) => {
    const c = r.data.data || empty;
    setData(c);
    setForm({ ...empty, ...c });
  }).catch(() => toast.error('Failed to load company details'));

  useEffect(() => { load(); }, []);

  const startEdit = () => { setForm({ ...empty, ...data }); setEditing(true); };
  const cancel = () => { setEditing(false); setForm({ ...empty, ...data }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.company_name.trim()) return toast.error('Company name is required');
    setSaving(true);
    try {
      await api.put('/company', form);
      toast.success('Company details saved');
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="empty">Loading company details...</div>;

  // ======= VIEW MODE =======
  if (!editing) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={18} /> Company Profile
          </h3>
          {data.can_edit && (
            <button className="btn btn-blue" onClick={startEdit}>
              <Edit2 size={14} /> Edit
            </button>
          )}
        </div>

        <div style={{ padding: 22 }}>
          {/* Header — company name & legal name */}
          <div style={{ paddingBottom: 18, borderBottom: '1px solid #e2e8f0', marginBottom: 18 }}>
            <h2 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>
              {data.company_name || <span style={{ color: '#cbd5e1' }}>Untitled company</span>}
            </h2>
            {data.legal_name && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{data.legal_name}</div>
            )}
            {!data.can_edit && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                View only — ask your admin to update these details.
              </div>
            )}
          </div>

          {/* Identification */}
          <div style={{ marginBottom: 18 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Identification
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <Field label="GSTIN" value={data.gstin} />
              <Field label="PAN" value={data.pan} />
              <Field label="Registration No." value={data.registration_no} />
            </div>
          </div>

          {/* Address */}
          <div style={{ marginBottom: 18 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Address
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 16 }}>
              <Field label="Street / Building" value={data.address} />
              <Field label="City" value={data.city} />
              <Field label="State" value={data.state} />
              <Field label="Pincode" value={data.pincode} />
              <Field label="Country" value={data.country} />
            </div>
          </div>

          {/* Contact */}
          <div style={{ marginBottom: 18 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Contact
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <Field label="Phone" value={data.phone} />
              <Field label="Email" value={data.email} />
              <Field label="Website" value={data.website} />
            </div>
          </div>

          {/* Accounting */}
          <div style={{ marginBottom: 18 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Accounting
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <Field label="Financial Year Start" value={data.financial_year_start} />
              <Field label="Currency" value={data.currency} />
            </div>
          </div>

          {data.notes && (
            <div>
              <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Notes
              </h4>
              <div style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-line' }}>{data.notes}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ======= EDIT MODE =======
  return (
    <div className="card">
      <div className="card-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 size={18} /> Edit Company Profile
        </h3>
        <div>
          <button className="btn btn-gray" onClick={cancel} disabled={saving}>
            <X size={14} /> Cancel
          </button>{' '}
          <button className="btn btn-blue" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ padding: 22 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Basic</h4>
        <div className="form-grid">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Company Name *</label>
            <input value={form.company_name} onChange={set('company_name')} required />
          </div>
          <div className="form-group">
            <label>Legal Name</label>
            <input value={form.legal_name} onChange={set('legal_name')} />
          </div>
          <div className="form-group">
            <label>Registration No.</label>
            <input value={form.registration_no} onChange={set('registration_no')} />
          </div>
          <div className="form-group">
            <label>GSTIN</label>
            <input value={form.gstin} onChange={set('gstin')} />
          </div>
          <div className="form-group">
            <label>PAN</label>
            <input value={form.pan} onChange={set('pan')} />
          </div>
        </div>

        <h4 style={{ margin: '18px 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Address</h4>
        <div className="form-grid">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Address</label>
            <textarea value={form.address} onChange={set('address')} rows={2} />
          </div>
          <div className="form-group"><label>City</label><input value={form.city} onChange={set('city')} /></div>
          <div className="form-group"><label>State</label><input value={form.state} onChange={set('state')} /></div>
          <div className="form-group"><label>Pincode</label><input value={form.pincode} onChange={set('pincode')} /></div>
          <div className="form-group"><label>Country</label><input value={form.country} onChange={set('country')} /></div>
        </div>

        <h4 style={{ margin: '18px 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Contact</h4>
        <div className="form-grid">
          <div className="form-group"><label>Phone</label><input value={form.phone} onChange={set('phone')} /></div>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={set('email')} /></div>
          <div className="form-group"><label>Website</label><input value={form.website} onChange={set('website')} placeholder="https://example.com" /></div>
        </div>

        <h4 style={{ margin: '18px 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Accounting</h4>
        <div className="form-grid">
          <div className="form-group">
            <label>Financial Year Start (MM-DD)</label>
            <input value={form.financial_year_start} onChange={set('financial_year_start')} placeholder="04-01" />
          </div>
          <div className="form-group">
            <label>Currency</label>
            <input value={form.currency} onChange={set('currency')} placeholder="INR" />
          </div>
        </div>

        <h4 style={{ margin: '18px 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</h4>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Optional internal notes about the company" />
        </div>
      </div>
    </div>
  );
}
